/**
 * Blueprint Data Structure and Validation
 * This defines the complete game blueprint schema used in the experimental game creator
 */

// Core blueprint interface
export const BlueprintSchema = {
  meta: {
    title: '',
    author: '',
    kernel: 'platformer', // platformer | topdown | card | puzzle | tactics
    mode: 'prototype', // prototype | full
    version: '0.1.0'
  },
  objectives: [], // Array of strings
  controls: {
    scheme: 'keyboard', // keyboard | touch
    map: {
      left: 'A',
      right: 'D',
      jump: 'SPACE'
    }
  },
  rules: [], // Array of rule objects
  levels: [], // Array of level objects
  assets: {
    packs: [], // Array of asset pack names
    overrides: {} // Object of asset overrides
  },
  progression: [], // Array of progression edges
  multiplayer: {
    enabled: false,
    mode: 'coop', // coop | pvp
    max_players: 1
  },
  cap_usage: {
    entities: 0,
    rules: 0
  },
  notes: ''
}

// Rule structure
export const RuleSchema = {
  id: '', // Unique identifier
  when: '', // trigger type
  a: '', // actor A (optional)
  b: '', // actor B / target (optional)
  do: '' // actions string
}

// Level structure
export const LevelSchema = {
  id: '', // Unique identifier
  theme: '', // Visual theme
  goal: '', // Level objective
  entities: [] // Array of entity objects
}

// Entity structure
export const EntitySchema = {
  id: '', // Unique identifier
  prefab: '', // Prefab type
  x: 0, // X position
  y: 0 // Y position
}

// Progression edge structure
export const ProgressionSchema = {
  from: '', // Source level ID
  to: '', // Target level ID
  condition: '' // Unlock condition
}

// Validation functions
export function validateBlueprint(blueprint) {
  const errors = []

  // Meta validation
  if (!blueprint.meta || !blueprint.meta.title) {
    errors.push('Blueprint must have a title')
  }

  if (!blueprint.meta || !blueprint.meta.kernel) {
    errors.push('Blueprint must specify a game kernel')
  }

  // Objectives validation
  if (!blueprint.objectives || blueprint.objectives.length === 0) {
    errors.push('Blueprint must have at least one objective')
  }

  // Rules validation
  if (!blueprint.rules || blueprint.rules.length === 0) {
    errors.push('Blueprint must have at least one rule')
  } else {
    blueprint.rules.forEach((rule, index) => {
      if (!rule.id) {
        errors.push(`Rule ${index + 1} must have an ID`)
      }
      if (!rule.when) {
        errors.push(`Rule ${index + 1} must specify when condition`)
      }
      if (!rule.do) {
        errors.push(`Rule ${index + 1} must specify actions`)
      }
    })
  }

  // Levels validation
  if (!blueprint.levels || blueprint.levels.length === 0) {
    errors.push('Blueprint must have at least one level')
  } else {
    blueprint.levels.forEach((level, index) => {
      if (!level.id) {
        errors.push(`Level ${index + 1} must have an ID`)
      }
      if (!level.theme) {
        errors.push(`Level ${index + 1} must have a theme`)
      }
    })
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

// Default blueprint template
export function createDefaultBlueprint() {
  return {
    meta: {
      title: 'My Awesome Game',
      author: '',
      kernel: 'platformer',
      mode: 'prototype',
      version: '0.1.0'
    },
    objectives: ['Complete the adventure'],
    controls: {
      scheme: 'keyboard',
      map: {
        left: 'A',
        right: 'D',
        jump: 'SPACE'
      }
    },
    rules: [
      {
        id: 'R1',
        when: 'overlap',
        a: 'player',
        b: 'gem',
        do: 'score+1, destroy(b)'
      }
    ],
    levels: [
      {
        id: 'level_1',
        theme: 'forest',
        goal: 'Collect 5 gems',
        entities: []
      }
    ],
    assets: {
      packs: ['starter-fantasy'],
      overrides: {}
    },
    progression: [],
    multiplayer: {
      enabled: false,
      mode: 'coop',
      max_players: 1
    },
    cap_usage: {
      entities: 0,
      rules: 0
    },
    notes: ''
  }
}

// Kernel definitions and their capabilities
export const KernelDefinitions = {
  platformer: {
    name: 'Platformer',
    description: '2D side-scrolling platformer game',
    requiredFields: ['controls', 'levels', 'rules'],
    supportedEngines: ['phaser'],
    defaultControls: {
      left: 'A',
      right: 'D',
      jump: 'SPACE'
    }
  },
  topdown: {
    name: 'Top-Down',
    description: 'Overhead view action game',
    requiredFields: ['controls', 'levels', 'rules'],
    supportedEngines: ['phaser'],
    defaultControls: {
      left: 'A',
      right: 'D',
      up: 'W',
      down: 'S'
    }
  },
  card: {
    name: 'Card Battler',
    description: 'Card-based strategy game',
    requiredFields: ['rules', 'assets'],
    supportedEngines: ['phaser']
  },
  puzzle: {
    name: 'Puzzle',
    description: 'Logic and puzzle-solving game',
    requiredFields: ['rules', 'levels'],
    supportedEngines: ['phaser', 'p5js']
  },
  tactics: {
    name: 'Tactics',
    description: 'Turn-based strategy game',
    requiredFields: ['rules', 'levels'],
    supportedEngines: ['phaser']
  }
}

// Asset pack definitions
export const AssetPacks = {
  'starter-fantasy': {
    name: 'Starter Fantasy',
    description: 'Basic fantasy-themed assets',
    assets: {
      player: 'hero',
      enemies: ['orc', 'skeleton'],
      items: ['gem', 'coin'],
      tiles: ['grass', 'stone', 'water']
    }
  },
  'pixel-arcade': {
    name: 'Pixel Arcade',
    description: 'Retro pixel art assets',
    assets: {
      player: 'pixel-hero',
      enemies: ['pixel-enemy'],
      items: ['pixel-coin'],
      tiles: ['pixel-grass', 'pixel-stone']
    }
  },
  'low-poly-kingdom': {
    name: 'Low Poly Kingdom',
    description: '3D low-poly medieval assets',
    assets: {
      player: 'knight',
      enemies: ['dragon', 'goblin'],
      items: ['crown', 'sword'],
      tiles: ['castle', 'village']
    }
  },
  'sci-fi-space': {
    name: 'Sci-Fi Space',
    description: 'Futuristic space-themed assets',
    assets: {
      player: 'astronaut',
      enemies: ['alien', 'robot'],
      items: ['energy-crystal', 'fuel-cell'],
      tiles: ['space-station', 'asteroid']
    }
  }
}

// Completion scoring
export function calculateCompletionScore(blueprint) {
  const sections = [
    { name: 'meta', weight: 10, check: () => blueprint.meta.title && blueprint.meta.kernel },
    { name: 'objectives', weight: 20, check: () => blueprint.objectives.length > 0 },
    { name: 'controls', weight: 15, check: () => blueprint.controls.scheme },
    { name: 'rules', weight: 25, check: () => blueprint.rules.length > 0 },
    { name: 'levels', weight: 20, check: () => blueprint.levels.length > 0 },
    { name: 'assets', weight: 10, check: () => blueprint.assets.packs.length > 0 }
  ]

  let totalScore = 0
  const completed = []

  sections.forEach(section => {
    if (section.check()) {
      totalScore += section.weight
      completed.push(section.name)
    }
  })

  return {
    score: totalScore,
    completed,
    total: 100,
    percentage: Math.round((totalScore / 100) * 100)
  }
}

// Export utility functions
export function exportBlueprintAsJSON(blueprint) {
  return JSON.stringify(blueprint, null, 2)
}

export function importBlueprintFromJSON(jsonString) {
  try {
    const blueprint = JSON.parse(jsonString)
    const validation = validateBlueprint(blueprint)
    if (!validation.isValid) {
      throw new Error(`Invalid blueprint: ${validation.errors.join(', ')}`)
    }
    return blueprint
  } catch (error) {
    throw new Error(`Failed to import blueprint: ${error.message}`)
  }
}
